import { AlertCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSkills } from "@/lib/useSkills";
import type { Skill } from "@/lib/types";

function SkillCard({ skill }: { skill: Skill }) {
  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <CardTitle>{skill.name}</CardTitle>
          <span className="shrink-0 rounded-pill bg-bw-blue-muted/20 px-3 py-1 font-sans text-xs font-semibold text-bw-ink-muted">
            {skill.status}
          </span>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-3 pt-0">
        {skill.description && (
          <p className="font-sans text-sm text-bw-ink-muted">{skill.description}</p>
        )}
        {skill.when_to_use && (
          <p className="font-sans text-xs text-bw-ink-muted">
            <span className="font-semibold">When to use:</span> {skill.when_to_use}
          </p>
        )}
        {skill.tags.length > 0 && (
          <div className="mt-auto flex flex-wrap gap-1.5 pt-2">
            {skill.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-pill bg-bw-blue/10 px-2.5 py-0.5 font-sans text-xs font-semibold text-bw-navy"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Skills — the reusable skill catalog (M1 Phase C browse surface). Read-only:
 * authoring lands in a later phase, and M2 serves these same rows to coding
 * agents over MCP, so today this mostly renders the empty state honestly.
 */
export function SkillsPage() {
  const { data, isLoading, isError, error } = useSkills();

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-3">
        <h1 className="text-4xl font-bold text-bw-navy">Skills</h1>
        <p className="max-w-2xl font-sans text-base text-bw-ink-muted">
          Reusable how-tos the team (and our coding agents) draw on — each with
          guidance on when it applies.
        </p>
      </section>

      {isLoading && <p className="font-sans text-sm text-bw-ink-muted">Loading skills…</p>}

      {isError && (
        <div className="flex items-center gap-2 rounded-card border border-bw-blue-muted/40 p-4 font-sans text-sm text-bw-ink-muted">
          <AlertCircle className="h-4 w-4 shrink-0 text-bw-blue" aria-hidden />
          {Number(error?.status) === 503
            ? "Skills aren't configured in this environment yet."
            : "Couldn't load skills. Try again shortly."}
        </div>
      )}

      {data && data.length === 0 && (
        <div className="rounded-card border border-bw-blue-muted/40 p-6 font-sans text-sm text-bw-ink-muted">
          No skills authored yet. The catalog fills in as the team captures
          reusable how-tos; they'll be served to coding agents over MCP in M2.
        </div>
      )}

      {data && data.length > 0 && (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((skill) => (
            <SkillCard key={skill.skill_id} skill={skill} />
          ))}
        </div>
      )}
    </div>
  );
}
